import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Breadcrumb, Button } from "flowbite-react";
import { HiHome } from "react-icons/hi";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import NavbarSidebarLayout from "@/layouts/navbar-sidebar";
import {
  useGetWorkShiftByIdQuery,
  useGetShiftAgentsQuery,
  useAssignShiftToAgentsMutation,
  useRemoveAgentFromShiftMutation,
} from "@/api/agentApi/workShift";
import ShiftAgentsTable from "@/components/workShifts/ShiftAgentsTable";
import AgentAssignmentDialog from "@/components/workShifts/AgentAssignmentDialog";
import ConfirmationDialog from "@/components/ConfirmationDialog";
import LoadingPage from "@/components/LoadingPage";
import { getErrorMessage } from "@/utils/requestUtils";
import { DataGridPagination } from "@/sharedTypes";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { agentRoles } from "@/constants/agent";

export default function WorkShiftDetails() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation(["workShift", "common"]);
  const user = useCurrentUser();
  const isAdmin = user?.agent_role === agentRoles.company_admin;
  const [page, setPage] = useState(1);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState<{
        title: string;
        body: string;
        onConfirm: () => void;
  } | null>(null);

  const { data: shift, isLoading: isShiftLoading } = useGetWorkShiftByIdQuery(id as string);
  
  const { data: shiftAgentsData, isLoading: isAgentsLoading } = useGetShiftAgentsQuery({
      shiftId: parseInt(id as string),
      params: {
          pagination: { page, pageSize: 15 },
      },
  }, { skip: !id });

  const [assignAgents] = useAssignShiftToAgentsMutation();
  const [removeAgent] = useRemoveAgentFromShiftMutation();

  const handleAssignAgents = (agentIds: number[]) => {
      if (!id) return;
      assignAgents({
          shift_id: parseInt(id),
          agent_ids: agentIds,
      })
      .unwrap()
      .then((res) => {
          const { assigned = [] } = res || {}; 
          if (assigned.length > 0) {
            toast.success(`Successfully assigned agents: ${assigned.join(", ")}`);
          } else {
            toast.success("Agents assigned successfully");
          }
          setIsAssignModalOpen(false);
      })
      .catch((err) => toast.error(getErrorMessage(err)));
  };

  const handleRemoveAgent = (agentId: number) => {
      if (!id) return;
      setConfirmDialog({
          title: t("workShift:removeAgentTitle") || "Remove Agent from Shift",
          body: t("workShift:removeAgentBody") || "Are you sure you want to remove this agent from the shift?",
          onConfirm: () => {
              removeAgent({ agentId, shiftId: parseInt(id) })
                  .unwrap()
                  .then(() => {
                      toast.success(t("workShift:removeAgentSuccess") || "Agent removed successfully");
                      setConfirmDialog(null);
                  })
                  .catch((err) => toast.error(getErrorMessage(err)));
          },
      });
  };

  const agents = shiftAgentsData?.items || shiftAgentsData?.data || [];
  const pagination = shiftAgentsData?.pagination || {};

  return (
    <NavbarSidebarLayout>
      {isShiftLoading ? <LoadingPage /> : <><div className="p-4 bg-white block sm:flex items-center justify-between border-b border-gray-200 lg:mt-1.5 dark:bg-gray-800 dark:border-gray-700">
        <div className="w-full mb-1">
          <div className="mb-4">
            <Breadcrumb className="mb-4">
              <Breadcrumb.Item>
                 <Link to="/" className="flex items-center gap-x-3">
                   <HiHome className="text-xl" />
                   <span className="dark:text-white">{t("common:home")}</span>
                 </Link>
              </Breadcrumb.Item>
              <Breadcrumb.Item>
                  <Link to="/work-shifts">{t("workShift:title")}</Link>
              </Breadcrumb.Item>
              <Breadcrumb.Item>
                  {shift?.shift_description || t("workShift:details")}
              </Breadcrumb.Item>
            </Breadcrumb>
            <h1 className="text-xl font-semibold text-gray-900 dark:text-white sm:text-2xl">
                {shift?.shift_description ? `${shift.shift_description} ${t("workShift:shiftDetails")}` : t("workShift:shiftDetails")}
            </h1>
          </div>
        </div>
      </div>

      <div className="p-4 space-y-4">
          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow sm:p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">{t("workShift:shiftInformation")}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                      <span className="font-medium text-gray-500 dark:text-gray-400">{t("workShift:description")}:</span>
                      <span className="ml-2 text-gray-900 dark:text-white">{shift?.shift_description}</span>
                  </div>
                  <div>
                      <span className="font-medium text-gray-500 dark:text-gray-400">{t("workShift:timezone")}:</span>
                      <span className="ml-2 text-gray-900 dark:text-white">{shift?.timezone}</span>
                  </div>
                  <div>
                      <span className="font-medium text-gray-500 dark:text-gray-400">{t("workShift:shiftHours")}:</span>
                      <span className="ml-2 text-gray-900 dark:text-white">{shift?.shift_hours_start} - {shift?.shift_hours_end}</span>
                  </div>
                  <div>
                      <span className="font-medium text-gray-500 dark:text-gray-400">{t("workShift:breakTime")}:</span>
                      <span className="ml-2 text-gray-900 dark:text-white">{shift?.break_time_start} - {shift?.break_time_end}</span>
                  </div>
              </div>
          </div>

          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow sm:p-6">
              <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{t("workShift:assignedAgents")}</h3>
                  {isAdmin && (
                      <Button color="primary" onClick={() => setIsAssignModalOpen(true)}>
                          {t("workShift:assignAgents")}
                      </Button>
                  )}
              </div>
              <ShiftAgentsTable
                  data={agents}
                  pagination={pagination as DataGridPagination}
                  onPageChange={setPage}
                  loading={isAgentsLoading}
                  onRemoveAgent={handleRemoveAgent}
              />
          </div>
      </div></>}

      <AgentAssignmentDialog
          open={isAssignModalOpen}
          onClose={() => setIsAssignModalOpen(false)}
          onSubmit={handleAssignAgents}
      />
      {confirmDialog && (
        <ConfirmationDialog
            onCancel={() => setConfirmDialog(null)}
            onConfirm={confirmDialog.onConfirm}
            title={confirmDialog.title}
            body={confirmDialog.body}
        />
      )}
    </NavbarSidebarLayout>
  );
}
